import type {PropertyValues} from 'lit';
import {html, LitElement} from 'lit';
import {customElement, property, query} from 'lit/decorators.js';
import type Map from 'ol/Map';
import {
  createPreviewMap,
  getEditableTrackFeatures,
  adaptPoiFeatures,
} from '../../map/helpers.ts';
import type {ClusteredLayerCode, LandCode} from '../../layers/layerTypes.ts';
import type {Ref} from 'lit/directives/ref.js';
import {createRef, ref} from 'lit/directives/ref.js';
import type {Extent} from 'ol/extent';
import type {Orientations} from '../../helpers/print.ts';
import {bgLayer$, visibleLayers$} from '../../stores/store-map.ts';
import {
  LANDSCAPE_PAGE_SIZES,
  POINTS_PER_INCH,
  PORTRAIT_PAGE_SIZES,
} from '../../helpers/print.ts';
import {pairwise, startWith} from 'rxjs';
import {
  activeTrackId$,
  currentlyActiveTourType$,
} from '../../stores/store-tours.ts';
import {
  fromEPSG4326,
  projectMultiLineStringCoordinates,
} from '../../map/proj.ts';
import {type DetailedTour, fetchTourAndPois} from '../../apis/api-tours.ts';
import {backendToPoiDetails} from '../../pages/page-map-tour-base.ts';
import {initTrackManager} from '../../map/track.ts';

const PORTRAIT_WIDTH = (300 / POINTS_PER_INCH) * PORTRAIT_PAGE_SIZES[0];
const PORTRAIT_HEIGHT = (300 / POINTS_PER_INCH) * PORTRAIT_PAGE_SIZES[1];
const LANDSCAPE_WIDTH = (300 / POINTS_PER_INCH) * LANDSCAPE_PAGE_SIZES[0];
const LANDSCAPE_HEIGHT = (300 / POINTS_PER_INCH) * LANDSCAPE_PAGE_SIZES[1];

@customElement('element-preview-map')
export class ElementPreviewMap extends LitElement {
  @property({type: String}) land: LandCode;
  @property({type: Array}) layers: ClusteredLayerCode[] | undefined;
  @property({type: Array}) extent: Extent | undefined;
  @property({type: String}) orientation: Orientations = 'landscape';
  @property({type: Boolean}) printBigOverlay = false;
  @query('.preview-map-container') mapContainer: HTMLElement;
  private resizeObserver: ResizeObserver;
  private mapContainerRef: Ref<HTMLDivElement> = createRef();
  private map: Map;

  override render() {
    return html`
      <div
        class="preview-map-container relative origin-top-left"
        ${ref(this.mapContainerRef)}
      ></div>
    `;
  }

  async loadMap() {
    const {map, layersManager} = createPreviewMap(
      this.extent,
      visibleLayers$.getValue(),
      bgLayer$.getValue(),
      1,
    );
    this.map = map;
    this.map.setTarget(this.mapContainerRef.value);
    bgLayer$.subscribe((bgLayer) => layersManager.activateBaseLayer(bgLayer));
    visibleLayers$
      .pipe(startWith(undefined), pairwise())
      .subscribe(([previousLayers, newLayers]) =>
        layersManager.activateLayers(previousLayers, newLayers),
      );

    // load track if present
    const trackId = activeTrackId$.value;
    if (trackId) {
      const tourType = currentlyActiveTourType$.value;

      let result;
      if (tourType === 'normal') {
        result = await fetchTourAndPois(trackId, 'normal');
      }
      if (tourType === 'recordedtrack') {
        result = await fetchTourAndPois(trackId, 'recordedtrack');
      }

      const tour = result.tour as DetailedTour;

      const coos2056 = projectMultiLineStringCoordinates(
        tour.geom,
        fromEPSG4326,
      );
      const trackFeatures = getEditableTrackFeatures(
        coos2056,
        tour.viapointsIndices,
        tour.id,
      );
      tour.pois = result.pois.features.map((p) => {
        const poi = backendToPoiDetails(p);
        poi.photoValidity = 'valid';
        return poi;
      });
      const tourPoiFeatures = adaptPoiFeatures(
        result.pois,
        tour.id,
        tour.properties.type,
      );
      const trackManager = initTrackManager(this.map, true);
      await trackManager.restoreFeatures([
        ...trackFeatures,
        ...tourPoiFeatures,
      ]);
    }
  }

  getPreviewMap() {
    return this.map;
  }

  override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('extent') && this.extent?.length) {
      if (!this.map) this.loadMap();
      if (this.map.isRendered() && !changedProperties.has('orientation')) {
        this.map.getView().fit(this.extent);
      } else {
        this.map.once('rendercomplete', () => {
          this.map.getView().fit(this.extent);
        });
      }
      this.map.render();
      this.updateSize();
    }
    super.updated(changedProperties);
  }

  override connectedCallback() {
    this.resizeObserver = new ResizeObserver(() => {
      this.updateSize();
    });
    this.resizeObserver.observe(document.body);
    super.connectedCallback();
  }

  override disconnectedCallback() {
    if (this.map) {
      this.map.setTarget(null);
      this.map = null;
    }
    this.resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  override createRenderRoot() {
    return this;
  }

  updateSize() {
    const landscape = this.orientation === 'landscape';
    const portrait = this.orientation === 'portrait';
    const landscapeWidth = this.printBigOverlay
      ? LANDSCAPE_WIDTH
      : LANDSCAPE_PAGE_SIZES[0];
    const portraitWidth = this.printBigOverlay
      ? PORTRAIT_WIDTH
      : PORTRAIT_PAGE_SIZES[0];
    const landscapeHeight = this.printBigOverlay
      ? LANDSCAPE_HEIGHT
      : LANDSCAPE_PAGE_SIZES[1];
    const portraitHeight = this.printBigOverlay
      ? PORTRAIT_HEIGHT
      : PORTRAIT_PAGE_SIZES[1];
    const width = landscape ? landscapeWidth : portraitWidth;
    const height = landscape ? landscapeHeight : portraitHeight;
    const parentWidth = this.parentElement.offsetWidth;
    const parentHeight = this.parentElement.offsetHeight;
    let scale = landscape ? parentWidth / width : parentHeight / height;
    const scaledWidth = width * scale;
    const scaledHeight = height * scale;
    if (scaledWidth > parentWidth && portrait) {
      scale = parentWidth / width;
    } else if (scaledHeight > parentHeight && landscape) {
      scale = parentHeight / height;
    }
    this.mapContainer.style.width = `${width}px`;
    this.mapContainer.style.height = `${height}px`;
    this.mapContainer.style.transform = `scale(${scale})`;
    const landscapeMargin = (parentHeight - scaledHeight) / 2;
    if (
      landscape &&
      this.printBigOverlay &&
      parentHeight > landscapeMargin + scaledHeight
    ) {
      this.mapContainer.style.marginTop = `${landscapeMargin}px`;
    } else {
      this.mapContainer.style.marginTop = '0';
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-preview-map': ElementPreviewMap;
  }
}
